import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EffectRef,
  ElementRef,
  inject,
  linkedSignal,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { PanelModule } from 'primeng/panel';
import { DmRangeDatePickerLibComponent } from '@everymatrix-base-components/dm-range-date-picker-lib';
import { DmInputTextLibComponent } from '@everymatrix-base-components/dm-input-text-lib';
import { DmSelectLibComponent } from '@everymatrix-base-components/dm-select-lib';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { VerificationService } from './services/verification.service';
import { DmTreeTableComponent } from '@everymatrix-base-components/dm-tree-table';
import { DmUiGridLibComponent } from '@everymatrix-base-components/dm-ui-grid-lib';
import { catchError, fromEvent, of, switchMap, take } from 'rxjs';
import { NgStyle } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  downloadURL,
  DynamicFilters,
  Lookups,
  REPORTS,
  ReportTemplate,
  ShadowDomStyleInjectorService,
  TableHeaderColumn,
  TemplateService,
} from '@everymatrix-base-components/utils';
import { Dialog } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import moment from 'moment';
import {
  ToggleSwitchChangeEvent,
  ToggleSwitchModule,
} from 'primeng/toggleswitch';
import {
  DomainsOverloadModalComponent,
  ReportConfigurationModalComponent,
  SaveTemplateModalComponent,
  TemplateSelectModalComponent,
  ToastMessageComponent,
} from '@everymatrix-base-components/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Checkbox } from 'primeng/checkbox';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    PanelModule,
    DmRangeDatePickerLibComponent,
    DmInputTextLibComponent,
    DmSelectLibComponent,
    DmButtonLibComponent,
    ReactiveFormsModule,
    DmTreeTableComponent,
    ProgressSpinnerModule,
    NgStyle,
    Dialog,
    TooltipModule,
    ToastMessageComponent,
    DmUiGridLibComponent,
    ToggleSwitchModule,
    FormsModule,
    SaveTemplateModalComponent,
    ReportConfigurationModalComponent,
    TemplateSelectModalComponent,
    DomainsOverloadModalComponent,
    Checkbox,
  ],
  providers: [TemplateService],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnInit, AfterViewInit {
  private templateService = inject(TemplateService);
  private verificationService = inject(VerificationService);
  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private shadowDomStyleInjectorService = inject(ShadowDomStyleInjectorService);
  attachedToShadowDom = signal(false);
  isTemplateSaved = signal(true);
  csvLoading = signal(false);
  templateCsvLoadingConfig = signal({ templateId: '', isLoading: false });
  configureReportModalVisible = signal(false);
  modalVisible = signal(false);
  domainsOverloadVisible = signal(false);
  lookupsSignal = signal<{ [key: string]: Lookups[] } | undefined>(undefined);
  domainLookupsSignal = signal<Lookups[] | undefined>(undefined);
  allFieldsSignal = signal<DynamicFilters[] | undefined>(undefined);
  templateSignal = this.templateService.template;
  ready = computed(() => {
    return (
      this.templateSignal() !== undefined &&
      this.lookupsSignal() !== undefined &&
      this.domainLookupsSignal() !== undefined &&
      this.allFieldsSignal() !== undefined
    );
  });
  destroyRef = inject(DestroyRef);
  visibleFieldsSignal: any = linkedSignal({
    source: () => this.allFieldsSignal(),
    computation: (all) => {
      if (all) {
        const selectedIds =
          this.templateSignal()?.input_params.visibleFields ??
          all?.filter((f) => f.is_default === 'true').map((f) => f.field_id);

        return all?.filter((f) => selectedIds.includes(f.field_id));
      }
      return undefined;
    },
  });
  allConfigurationFields!: DynamicFilters[];
  requiredFields = [
    'merchant_name',
    'verification_creation_date',
    'verification_updated_date',
  ];
  tableColumns: TableHeaderColumn[] = [];
  filterForm: any;
  isPanelOpened = true;
  tableData: any[] = [];
  generalTotal = '';
  limit = signal(100);
  offset = 0;
  showReportTable = signal(false);
  tableLoading = false;
  runState = false;
  documentsModal = false;
  isOldTableStyle = false;
  domainsOverloadSaveMode = true;
  gridApi: any;
  orderBy: any;
  orderDirection: any;
  reportId = REPORTS.MM_VERIFICATION.reportId;
  effectRef: EffectRef;
  gridOptions: any = {
    infiniteScrollDown: false,
    enableSorting: true,
    showGridFooter: false,
    showColumnFooter: true,
    enableFiltering: false,
    enableColumnResizing: true,
    enableGridMenu: true,
    exporterMenuCsv: false,
    exporterMenuPdf: false,
    exporterMenuExcel: false,
    treeRowHeaderAlwaysVisible: true,
    columnDefs: [],
    data: [],
    enableHorizontalScrollbar: 2,
    gridFooterTemplate: `<div class="ui-grid-footer-info ui-grid-grid-footer">
                    <div ng-if="grid.loadingAggregationData">Loading total...</div>
                    <div ng-if="!grid.loadingAggregationData">Total: {{grid.total}} (visible: {{grid.rows.length}})</div>
                </div>`,
    enableRowHeaderSelection: false,
    useExternalSorting: true,
    dataLoading: this.tableLoading,
    isVisible: false,
    paginationPageSize: this.limit(),
    paginationPageSizes: [10, 20, 30, 50, 100],
    useExternalPagination: true,
    totalItems: 0,
    paginationCurrentPage: 1,
    onRegisterApi: (gridApi: any) => {
      this.gridApi = gridApi;
      this.gridApi.core.on.sortChanged(null, (grid: any, sortColumn: any) => {
        if (sortColumn.length > 1) {
          let column = null;
          for (let j = 0; j < grid.columns.length; j++) {
            if (grid.columns[j].name === sortColumn[0].displayName) {
              column = grid.columns[j];
              break;
            }
          }
          if (column) {
            sortColumn[1].sort.priority = 1;
            column.unsort();
          }
        } else if (sortColumn && sortColumn[0]) {
          this.onSort({
            field: sortColumn.slice(-1)[0].field,
            value: sortColumn[0].sort.direction,
          });
        }
      });
      if (this.gridApi) {
        this.gridApi.grid.registerDataChangeCallback(() =>
          this.gridApi.treeBase.expandAllRows()
        );

        this.gridApi.pagination.on.paginationChanged(
          null,
          (newPage: number, pageSize: number) => {
            // do not trigger data loading if only need to set correct page
            if (
              this.limit() === pageSize &&
              (newPage - 1) * pageSize == this.offset
            )
              return;

            this.limit.set(pageSize);
            this.runReport((newPage - 1) * pageSize, true);
          }
        );
      }
    },
  };

  constructor() {
    this.effectRef = effect(
      () => {
        if (!this.ready() || this.filterForm) {
          return;
        }
        const tpl = this.templateSignal();
        const all = this.allFieldsSignal();
        this.buildFilterForm(
          all as DynamicFilters[],
          tpl?.input_params.fieldValues
        );
        this.showReportTable.set(false);
        this.tableLoading = false;
        this.effectRef.destroy();
      },
      { manualCleanup: true }
    );
  }

  ngOnInit() {
    this.processTemplateIdFromUrl();
    //Executed when the back/forward buttons are pressed
    fromEvent(window, 'popstate')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleRouteChange());

    this.verificationService.initData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data?.lookups) {
          if (!this.lookupsSignal()) {
            this.lookupsSignal.set(this.groupLookups(data.lookups));
          }
        }
        if (data?.domainLookups) {
          if (!this.domainLookupsSignal()) {
            this.domainLookupsSignal.set(data.domainLookups);
          }
        }

        if (data?.columns) {
          if (!this.allFieldsSignal()) {
            const result = this.sortRequiredFields(data.columns.result);
            result.forEach((col: DynamicFilters) => {
              if (this.requiredFields.includes(col.field_id)) {
                col.required = true;
              }
            });
            this.allFieldsSignal.set(result);
            this.allFieldsForConfiguration(result);
          }
        }
      });

    this.templateService.templateChangedSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((template: ReportTemplate | null) => {
        if (template) {
          this.updateReport(template);
        }
      });
  }

  private handleRouteChange = () => {
    const params = new URLSearchParams(window.location.search);
    const templateId = params.get('templateId');

    if (templateId) {
      const currentTemplate = (
        this.templateService.templatesList() as ReportTemplate[]
      )?.find((item) => item.template_id === templateId);
      this.templateSignal.set(currentTemplate);
      this.templateService.templateChangedSubject.next(
        currentTemplate as ReportTemplate
      );
    } else {
      this.clearFilters();
      this.templateSignal.set(null);
    }
  };

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService.injectUiGridStyles(this.el);
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.el)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (injected) => this.attachedToShadowDom.set(injected),
        error: (err) => console.error(err),
      });
  }

  updateReport(template: ReportTemplate) {
    this.filterForm.patchValue(template.input_params.fieldValues);
    this.runState = false;
    this.showReportTable.set(false);
    this.isTemplateSaved.set(true);
  }

  private processTemplateIdFromUrl() {
    const queryParams = new URLSearchParams(window.location.search);
    const templateId = queryParams.get('templateId');

    if (templateId) {
      this.templateService
        .getTemplateById(this.reportId, templateId)
        .pipe(take(1))
        .subscribe();
    } else {
      this.templateSignal.set(null);
    }
  }

  protected saveTemporaryTemplate() {
    const payload = {
      fieldValues: this.filterForm.value,
      visibleFields: this.visibleFieldsId,
      allFields: this.allFieldsSignal(),
    };
    if (!this.isTemplateSaved() || !this.templateSignal()) {
      this.templateService
        .saveTemplate(null, null, payload, this.reportId, null, true)
        .pipe(take(1))
        .subscribe((template) => {
          this.templateService.setUrlTemplateId(template.template_id);
          this.templateService.template.set(template);
        });
    }
  }

  private sortRequiredFields(columns: DynamicFilters[]) {
    const mandatoryLength = this.requiredFields.length;
    const getPriority = (fieldId: string) => {
      const index = this.requiredFields.indexOf(fieldId);
      return index !== -1 ? index : mandatoryLength;
    };

    columns.sort((a, b) => {
      return getPriority(a.field_id) - getPriority(b.field_id);
    });

    return columns;
  }

  private buildFilterForm(
    all: DynamicFilters[],
    values?: Record<string, unknown>
  ) {
    this.filterForm = new FormGroup({});
    all.forEach((field) => {
      let def = (values && values[field.field_id]) || null;

      const validators = [];

      if (field.field_id === 'merchant_name') {
        validators.push(Validators.required);
        if (!values) {
          def = (this.domainLookupsSignal() as Lookups[]).map(
            (l) => l.lookup_key
          );
        }
      }
      if (!values && field.field_id === 'verification_creation_date') {
        def = {
          alias: 'custom',
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate(),
        };
      }

      this.filterForm.addControl(
        field.field_id,
        new FormControl(def, validators)
      );
    });

    this.filterForm.addControl('show_last_verification', new FormControl(true));

    if (values) {
      this.isTemplateSaved.set(true);
    }

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isTemplateSaved.set(false));
  }

  private buildOldTableColumns(rawColumns: DynamicFilters[]): void {
    this.gridOptions.columnDefs = rawColumns.map((item, index) => {
      return {
        name: item.field_name,
        displayName: item.field_name,
        field: item.field_id,
        cellTooltip: true,
        orderNumber: index + 1,
        enableSorting: item.field_id !== 'documents',
        width: '*',
        minWidth: 200,
        enableGrouping: false,
        cellClass:
          item['field_data_type'] === 'BIGNUMERIC' ||
          item['field_data_type'] === 'INT64'
            ? 'text-align-right'
            : 'text-align-left',
      };
    });

    const merchant_id = {
      name: 'Merchant ID',
      displayName: 'Merchant ID',
      field: 'merchant_id',
      cellTooltip: true,
      orderNumber: 0,
      width: '*',
      minWidth: 200,
      enableGrouping: false,
      cellClass: 'text-align-right',
    };
    const version = {
      name: 'Version',
      displayName: 'Version',
      field: 'version',
      cellTooltip: true,
      orderNumber: this.gridOptions.columnDefs.length,
      width: '*',
      minWidth: 200,
      enableGrouping: false,
      cellClass: 'text-align-right',
    };
    this.gridOptions.columnDefs.unshift(merchant_id);
    this.gridOptions.columnDefs.push(version);
  }

  private buildTableColumns(visible: DynamicFilters[]) {
    this.tableColumns = visible.map((f, i) => ({
      name: f.field_name,
      field: f.field_id,
      headerTooltip: '',
      visible: true,
      cellClass:
        f['field_data_type'] === 'BIGNUMERIC' ||
        f['field_data_type'] === 'INT64'
          ? 'text-align-right'
          : 'text-align-left',
      footerCellClass: '',
      minWidth: 200,
      sorting: f.field_id !== 'documents',
      orderNumber: i + 1,
    }));

    this.tableColumns.unshift({
      name: 'Merchant ID',
      field: 'merchant_id',
      headerTooltip: '',
      visible: true,
      cellClass: 'text-align-right',
      footerCellClass: '',
      minWidth: 200,
      sorting: true,
      orderNumber: 0,
    });
    this.tableColumns.push({
      name: 'Version',
      field: 'version',
      headerTooltip: '',
      visible: true,
      cellClass: 'text-align-right',
      footerCellClass: '',
      minWidth: 200,
      sorting: true,
      orderNumber: this.tableColumns.length + 1,
    });
  }

  protected runReport(offset: number, isLoadingMore = false) {
    this.showReportTable.set(true);

    this.buildTableColumns(this.visibleFieldsSignal() as DynamicFilters[]);
    this.buildOldTableColumns(this.visibleFieldsSignal() as DynamicFilters[]);

    this.offset = offset;
    this.tableLoading = true;
    this.gridOptions.dataLoading = true;
    this.cdr.detectChanges();
    this.runState = true;
    if (isLoadingMore) {
      this.gridOptions.paginationPageSize = this.limit();
      this.gridOptions.paginationCurrentPage = this.getPageFromOffset(this.offset, this.limit());
    } else {
      this.orderBy = null;
      this.orderDirection = null;
    }

    const params = {
      offset,
      limit: this.limit(),
      allFilters: this.allFieldsSignal() as DynamicFilters[],
      formData: this.filterForm.value,
      columns: this.visibleFieldsId,
      executionType: 'report',
      sorting: { field: this.orderBy, value: this.orderDirection },
      template:
        this.templateService.template() &&
        !this.templateService.template()?.is_temporary
          ? this.templateService.template()
          : null,
    };
    this.verificationService
      .runReport(params)
      .pipe(
        take(1),
        catchError(() => {
          return of([]);
        })
      )
      .subscribe((data: any) => {
        this.tableLoading = false;
        this.gridOptions.dataLoading = false;
        this.tableData = [...data];
        this.gridOptions.data = [...data];
        this.runState = false;
        this.cdr.detectChanges();
      });
    if (!isLoadingMore) {
      this.loadGrandTotal(offset);
    }
  }

  protected loadGrandTotal(offset: number) {
    if (!offset) {
      if (this.gridApi) {
        this.gridApi.grid.loadingAggregationData = true;
      }
      const params = {
        offset,
        limit: this.limit(),
        allFilters: this.allFieldsSignal() as DynamicFilters[],
        formData: this.filterForm.value,
        columns: this.visibleFieldsId,
        executionType: 'report',
        isGrandTotal: true,
        sorting: { field: this.orderBy, value: this.orderDirection },
      };
      this.verificationService
        .runReport({
          ...params,
          isGrandTotal: true,
        })
        .pipe(
          take(1),
          catchError(() => of([]))
        )
        .subscribe((data) => {
          this.generalTotal = data[0]?.rows_count;
          if (this.gridApi) {
            this.gridApi.grid.total =
              data && data[0] && data[0].rows_count ? data[0].rows_count : 0;
            this.gridOptions.totalItems =
              data && data[0] && data[0].rows_count ? data[0].rows_count : 0;
            this.gridApi.grid.loadingAggregationData = false;
          }
          this.cdr.detectChanges();
        });
    }
  }

  protected onSort(sortingValue: {
    field: string | null;
    value: 'asc' | 'desc' | null;
  }): void {
    this.offset = 0;
    this.tableLoading = true;
    this.gridOptions.dataLoading = true;
    this.orderBy = sortingValue.field;
    this.orderDirection = sortingValue.value;
    this.gridOptions.paginationCurrentPage = this.getPageFromOffset(this.offset, this.limit());
    this.verificationService
      .runReport({
        offset: 0,
        limit: this.limit(),
        allFilters: this.allFieldsSignal() as DynamicFilters[],
        formData: this.filterForm.value,
        columns: this.visibleFieldsId,
        executionType: 'report',
        isGrandTotal: false,
        sorting: sortingValue,
      })
      .pipe(
        take(1),
        catchError(() => of([]))
      )
      .subscribe((data) => {
        this.tableData = [...data];
        this.gridOptions.data = [...data];
        this.tableLoading = false;
        this.gridOptions.dataLoading = false;
        this.cdr.detectChanges();
      });
  }

  protected downloadCsv(template?: ReportTemplate) {
    this.verificationService
      .runReport({
        offset: 0,
        limit: this.limit(),
        allFilters: template
          ? template.input_params.allFields
          : (this.allFieldsSignal() as DynamicFilters[]),
        formData: template
          ? template.input_params.fieldValues
          : this.filterForm.value,
        columns: template
          ? template.input_params.visibleFields
          : this.visibleFieldsId,
        executionType: 'csv_load',
        isGrandTotal: false,
        sorting: { field: null, value: null },
      })
      .pipe(
        take(1),
        switchMap((data) => this.verificationService.csvLoad(data[0].path)),
        catchError((e) => {
          this.csvLoading.set(false);
          if (template) {
            this.templateCsvLoadingConfig.set({
              templateId: template.template_id,
              isLoading: false,
            });
          }
          return e;
        })
      )
      .subscribe((res: any) => {
        const templateName = `Report_${
          template?.template_name ||
          (this.templateSignal() as ReportTemplate).template_name ||
          ''
        }_${moment().format('YYYY-MM-DD_HH:mm')}.csv`;
        downloadURL(res, templateName);
        this.csvLoading.set(false);
        if (template) {
          this.templateCsvLoadingConfig.set({
            templateId: template.template_id,
            isLoading: false,
          });
        }
      });
  }

  private groupLookups(data: Lookups[]): { [k: string]: Lookups[] } {
    return data.reduce((acc, cur) => {
      (acc[cur.param_key] = acc[cur.param_key] || []).push(cur);
      return acc;
    }, {} as Record<string, Lookups[]>);
  }

  protected getControl(fieldId: string): any {
    const control = this.filterForm.get(fieldId);
    return control instanceof FormControl ? control : null;
  }

  getFilledFieldNames() {
    return (this.allFieldsSignal() as DynamicFilters[])
      .filter(({ field_id }) => {
        const value = this.filterForm.value[field_id];
        if (value == null) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'string') {
          return value.length > 0;
        }
        return true;
      })
      .map(({ field_name }) => field_name)
      .join(', ');
  }

  protected clearFilters(): void {
    const defaults: Record<string, any> = {
      merchant_name: this.domainLookupsSignal()!.map((l) => l.lookup_key),
      verification_creation_date: {
        alias: 'custom',
        start: moment().startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
      },
      show_last_verification: true,
    };
    this.filterForm.reset(defaults);
  }

  protected calculateHeightPosition(element: HTMLElement): void {
    const content = element.parentElement as HTMLElement;
    setTimeout(() => {
      content.scrollTop = content.scrollHeight;
    }, 100);
  }

  public onToggleChange(event: ToggleSwitchChangeEvent) {
    this.gridOptions.isVisible = event.checked;
    this.cdr.detectChanges();
  }

  public onDocumentsClear(): void {
    this.getControl('documents').reset();
    this.getControl('documents_type').reset();
    this.getControl('documents_status').reset();
    this.getControl('documents_expiry_date').reset();
    this.getControl('documents_created_date').reset();
  }

  get visibleFieldsId(): string[] {
    if (this.visibleFieldsSignal()) {
      return (this.visibleFieldsSignal() as DynamicFilters[]).map(
        (f) => f.field_id
      );
    } else {
      return [];
    }
  }

  protected openSaveTemplateModal() {
    this.domainsOverloadSaveMode = true;
    if (this.isAllDomainsSelected) {
      this.domainsOverloadVisible.set(true);
    } else {
      this.modalVisible.set(true);
    }
  }

  protected runOverloadCheck() {
    if (this.isAllDomainsSelected) {
      this.domainsOverloadSaveMode = false;
      this.domainsOverloadVisible.set(true);
    } else {
      this.saveTemporaryTemplate();
      this.runReport(0, false);
    }
  }

  protected domainsOverloadConfirm(isSaveMode: boolean): void {
    if (isSaveMode) {
      this.modalVisible.set(true);
    } else {
      this.saveTemporaryTemplate();
      this.runReport(0, false);
    }
  }

  get isAllDomainsSelected(): boolean {
    return (
      this.domainLookupsSignal()?.length ===
      this.filterForm.get('merchant_name').value.length
    );
  }

  protected applyConfigurationFields(event: {
    fieldsToShow: DynamicFilters[];
    allFields: DynamicFilters[];
  }) {
    this.isTemplateSaved.set(false);
    this.visibleFieldsSignal.set(event.fieldsToShow);
  }

  protected applyDocumentField() {
    const fieldsMap: Record<string, string> = {
      documents_type: 'Type',
      documents_status: 'Status',
      documents_expiry_date: 'Expiry Date',
      documents_created_date: 'Updated Date',
    };

    const result = Object.entries(fieldsMap)
      .filter(([field]) => !!this.filterForm.get(field)?.value)
      .map(([, label]) => label)
      .join(', ');

    this.filterForm.get('documents')?.patchValue(result);
  }

  private allFieldsForConfiguration(data: DynamicFilters[]) {
    const fieldsToExclude = [
      'documents_type',
      'documents_status',
      'documents_expiry_date',
      'documents_created_date',
    ];
    this.allConfigurationFields = data.filter(
      (item) => !fieldsToExclude.includes(item.field_id)
    );
  }

  private getPageFromOffset(offset: number, limit: number) {
    return offset ? Math.floor(offset / limit) + 1 : 1
  }
}
